import type { ModeController } from '../ModeController';
import type { AppMode } from '../../types';
import { UnifiedCanvas } from './UnifiedCanvas';
import { UnifiedStore } from './UnifiedStore';

export interface UnifiedModeContext {
  getCanvas: () => HTMLCanvasElement | null;
  getToolbar: () => HTMLElement | null;
  getUnifiedStore: () => UnifiedStore;
  getMode: () => AppMode;
  setMode: (mode: AppMode) => void;
  setupToolbar: (canvas: UnifiedCanvas, mode: AppMode) => void;
  onScheduleAutoSave: () => void;
}

export class UnifiedModeController implements ModeController {
  private readonly context: UnifiedModeContext;
  private canvas: UnifiedCanvas | null = null;

  constructor(context: UnifiedModeContext) {
    this.context = context;
  }

  getCanvas(): UnifiedCanvas | null {
    return this.canvas;
  }

  init(): void {
    const canvasEl = this.context.getCanvas();
    if (!canvasEl) return;

    if (!this.canvas) {
      // Create canvas once
      this.canvas = new UnifiedCanvas(canvasEl, this.context.getUnifiedStore());
      this.canvas.setOnChange(() => this.context.onScheduleAutoSave());
    }

    const mode = this.context.getMode();
    this.canvas.setActiveMode(mode);
    this.context.setupToolbar(this.canvas, mode);
  }

  teardown(): void {
    // No-op: canvas stays alive, only toolbar changes
  }

  restoreState(inverted: boolean, state: string | null, _fallback: boolean): void {
    if (!this.canvas) return;

    if (state) {
      this.canvas.loadState(state);
    }

    // Sync inverted state
    if (this.canvas.isInverted() !== inverted) {
      this.canvas.setInverted(inverted);
    }

    // Use the mode restored from saved state (if available), not the app default
    const mode = state ? this.canvas.getActiveMode() : this.context.getMode();
    this.context.setMode(mode);
    if (this.canvas.getActiveMode() !== mode) {
      this.canvas.setActiveMode(mode);
    }
    this.context.setupToolbar(this.canvas, mode);
  }

  getState(): string | null {
    return this.canvas?.getState() ?? null;
  }
}
